import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import api from '../api/api';

export default function CalendarPage() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publications, setPublications] = useState([]);
  const [positions, setPositions] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [clients, setClients] = useState([]);

  const [filters, setFilters] = useState({
    mediaResourceId: '',
    positionId: '',
    statusId: '',
    clientId: '',
  });

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      borderRadius: '0.75rem',
      padding: '0.1rem',
      minWidth: '200px',
      borderColor: '#e2e8f0',
      boxShadow: 'none',
      '&:hover': {
        borderColor: '#3b82f6'
      }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#eff6ff' : 'white',
      color: state.isSelected ? 'white' : '#1e293b',
      '&:active': {
        backgroundColor: '#3b82f6'
      }
    })
  };

  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(today.setDate(diff));
    monday.setHours(0, 0, 0, 0);
    return monday;
  });

  // Calculate the week dates based on the currentWeekStart
  const weekDates = useMemo(() => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(currentWeekStart);
      d.setDate(currentWeekStart.getDate() + i);
      dates.push(d);
    }
    return dates;
  }, [currentWeekStart]);

  const handlePrevWeek = () => {
    setCurrentWeekStart(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() - 7);
      return d;
    });
  };

  const handleNextWeek = () => {
    setCurrentWeekStart(prev => {
      const d = new Date(prev);
      d.setDate(d.getDate() + 7);
      return d;
    });
  };

  const days = useMemo(() => {
    return weekDates.map(d => {
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      const weekday = d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
      return `${weekday} ${day}/${month}/${year}`;
    });
  }, [weekDates]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [bookingsRes, pubRes, posRes, statusRes, clientRes] = await Promise.all([
          api.get('/bookings', { 
            params: { 
              mediaType: 'PRINT', 
              limit: 1000,
              mediaResourceId: filters.mediaResourceId || undefined,
              positionId: filters.positionId || undefined,
              statusId: filters.statusId || undefined,
              clientId: filters.clientId || undefined,
            } 
          }),
          api.get('/master/media-resources'),
          api.get('/master/positions'),
          api.get('/statuses'),
          api.get('/master/clients'),
        ]);
        setBookings(bookingsRes.data.bookings || []);
        setPublications(pubRes.data.resources || []);
        setPositions(posRes.data.positions || []);
        setStatuses(statusRes.data.statuses || []);
        setClients(clientRes.data.clients || []);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filters, currentWeekStart]);

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const pubOptions = [
    { value: '', label: 'All Publications' },
    ...publications.map(p => ({ value: p.id, label: p.name }))
  ];

  const posOptions = [
    { value: '', label: 'All Positions' },
    ...positions.map(p => ({ value: p.id, label: p.name }))
  ];

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    ...statuses.map(s => ({ value: s.id, label: s.name }))
  ];

  const clientOptions = [
    { value: '', label: 'All Clients' },
    ...clients.map(c => ({ value: c.id, label: c.name }))
  ];

  const getStatusIcon = (statusName) => {
    const name = statusName?.toLowerCase();
    if (name === 'completed') return '✅';
    if (name === 'cancelled') return '❌';
    if (name === 'pending' || name === 'booked') return '🕒';
    if (name === 'confirmed') return '✨';
    return '🕒';
  };

  // Group bookings by Publication + Position for the rows
  const calendarRows = useMemo(() => {
    const rows = [];
    const uniquePairs = new Set();
    bookings.forEach(b => {
      if (b.media_type === 'PRINT') {
        const key = `${b.media_resource_id}-${b.position_id}`;
        if (!uniquePairs.has(key)) {
          uniquePairs.add(key);
          rows.push({
            pubId: b.media_resource_id,
            pubName: b.mediaName,
            posId: b.position_id,
            posName: b.positionName
          });
        }
      }
    });

    // Sort by Publication Name, then Position Name
    return rows.sort((a, b) => {
      const pubA = (a.pubName || '').toLowerCase();
      const pubB = (b.pubName || '').toLowerCase();
      if (pubA < pubB) return -1;
      if (pubA > pubB) return 1;

      const posA = (a.posName || '').toLowerCase();
      const posB = (b.posName || '').toLowerCase();
      if (posA < posB) return -1;
      if (posA > posB) return 1;

      return 0;
    });
  }, [bookings]);

  const getBookingForCell = (row, date) => {
    // Use local date string (YYYY-MM-DD) to avoid timezone shifts
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    return bookings.filter(b => 
      b.media_resource_id === row.pubId && 
      b.position_id === row.posId &&
      b.releaseDates?.some(r => {
        const rDate = new Date(r.release_date);
        const rYear = rDate.getFullYear();
        const rMonth = String(rDate.getMonth() + 1).padStart(2, '0');
        const rDay = String(rDate.getDate()).padStart(2, '0');
        const rDateStr = `${rYear}-${rMonth}-${rDay}`;
        return rDateStr === dateStr;
      })
    );
  };

  const formatDisplayDate = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  return (
    <div className="calendar-page">
      <header className="calendar-header">
        <div>
          <h2>Print Calendar</h2>
          <p>{new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(weekDates[0])} - Print Media Bookings</p>
        </div>
      </header>

      <div className="reports-filters-card" style={{ marginBottom: '1.5rem', padding: '1.25rem', background: '#fff', borderRadius: '1rem', border: '1px solid #e2e8f0' }}>
        <div className="filters-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem' }}>
          <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>Client</label>
            <Select
              options={clientOptions}
              value={clientOptions.find(opt => opt.value === filters.clientId)}
              onChange={(opt) => handleFilterChange('clientId', opt.value)}
              placeholder="Search Client..."
              styles={customSelectStyles}
              isSearchable
            />
          </div>
          <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>Publication</label>
            <Select
              options={pubOptions}
              value={pubOptions.find(opt => opt.value === filters.mediaResourceId)}
              onChange={(opt) => handleFilterChange('mediaResourceId', opt.value)}
              placeholder="Search Publication..."
              styles={customSelectStyles}
              isSearchable
            />
          </div>
          <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>Position</label>
            <Select
              options={posOptions}
              value={posOptions.find(opt => opt.value === filters.positionId)}
              onChange={(opt) => handleFilterChange('positionId', opt.value)}
              placeholder="Search Position..."
              styles={customSelectStyles}
              isSearchable
            />
          </div>
          <div className="filter-group" style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569' }}>Status</label>
            <Select
              options={statusOptions}
              value={statusOptions.find(opt => opt.value === filters.statusId)}
              onChange={(opt) => handleFilterChange('statusId', opt.value)}
              placeholder="Search Status..."
              styles={customSelectStyles}
              isSearchable
            />
          </div>
        </div>
      </div>

      <div className="calendar-grid-card">
        <div style={{ 
          padding: '1rem 1.25rem', 
          borderBottom: '1px solid #e2e8f0', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          background: '#f8fafc'
        }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>
            Week of {formatDisplayDate(weekDates[0])} - {formatDisplayDate(weekDates[6])}
          </h3>
          <div className="calendar-legend" style={{ margin: 0, background: '#fff' }}>
            <div className="legend-item">
              <div className="legend-color" style={{ background: '#eff6ff', border: '1px solid #dbeafe' }}></div>
              <span>Agency</span>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ background: '#f0fdfa', border: '1px solid #ccfbf1' }}></div>
              <span>Publication</span>
            </div>
            <div className="legend-item">
              <div className="legend-color" style={{ background: '#fff7ed', border: '2px solid #f97316' }}></div>
              <span>Queued</span>
            </div>
          </div>
          <div className="calendar-nav" style={{ margin: 0, background: '#fff' }}>
            <button className="nav-btn" onClick={handlePrevWeek} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.8rem' }}>
              <span style={{ fontSize: '1.1rem' }}>‹</span> Previous Week
            </button>
            <div style={{ width: '1px', height: '20px', background: '#e2e8f0', margin: '0 0.2rem' }}></div>
            <button className="nav-btn" onClick={handleNextWeek} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.4rem 0.8rem' }}>
              Next Week <span style={{ fontSize: '1.1rem' }}>›</span>
            </button>
          </div>
        </div>
        <div className="scroll-container">
          <table className="calendar-table">
            <thead>
              <tr>
                <th>PUBLICATION / POSITION</th>
                {days.map(day => <th key={day}>{day}</th>)}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="8" style={{ textAlign: 'center', height: '200px' }}>Loading calendar...</td></tr>
              ) : calendarRows.length > 0 ? (
                calendarRows.map((row, idx) => (
                  <tr key={`${row.pubId}-${row.posId}-${idx}`}>
                    <td>
                      <div className="row-resource-info">
                        <span className="row-resource-name">{row.pubName || 'Unnamed Publication'}</span>
                        <span className="row-position-name">{row.posName || 'Unnamed Position'}</span>
                      </div>
                    </td>
                    {weekDates.map(date => {
                      const year = date.getFullYear();
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const day = String(date.getDate()).padStart(2, '0');
                      const dateStr = `${year}-${month}-${day}`;

                      const cellBookings = getBookingForCell(row, date);
                      
                      return (
                        <td key={date.toISOString()}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {cellBookings.map(booking => {
                              const release = booking.releaseDates?.find(r => {
                                const rDate = new Date(r.release_date);
                                const rYear = rDate.getFullYear();
                                const rMonth = String(rDate.getMonth() + 1).padStart(2, '0');
                                const rDay = String(rDate.getDate()).padStart(2, '0');
                                const rDateStr = `${rYear}-${rMonth}-${rDay}`;
                                return rDateStr === dateStr;
                              });
                              
                              const isQueued = booking.notes && booking.notes.includes('[Queue Position: Booking');
                              
                              return (
                                <div 
                                  key={booking.id}
                                  className={`calendar-card ${booking.booking_source?.toLowerCase()} ${isQueued ? 'queued' : ''}`}
                                  onClick={() => navigate(`/bookings/view/${booking.id}`)}
                                  style={isQueued ? { 
                                    background: '#fff7ed', 
                                    borderColor: '#f97316', 
                                    color: '#c2410c',
                                    borderWidth: '2px'
                                  } : {}}
                                >
                                  <span className="card-client" style={{ fontSize: '0.75rem' }}>
                                    {isQueued ? `Q${booking.notes.match(/Booking (\d+)/)?.[1]}: ` : ''}
                                    {booking.clientName || booking.new_client_name || 'Client'}
                                  </span>
                                  <span className="card-edition" style={{ fontSize: '0.7rem' }}>{booking.editionName || 'Edition'}</span>
                                  <span className="card-status-icon">
                                    {getStatusIcon(release?.status_name || booking.status_name)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))
              ) : (
                <tr><td colSpan="8" style={{ textAlign: 'center', height: '200px' }}>No bookings found for this period.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}
